import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Project } from 'src/app/models/project';
import { ProjectDuplicateTarget } from 'src/app/models/project-duplicate-target';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { environment } from 'src/environments/environment';
import { CurrentService } from '../current.service';
import { CustomerService } from '../customer.service';
import { CustomerSession } from '../customer-session';
import { logError } from '../helpers/sentry.helper';
import { Customer } from '../kingfisher/customer';
import { MODAL, ModalService } from '../modal.service';
import { ProjectService } from '../project.service';
import { HbmConfiguratorRegistry } from './hbm-configurator-registry.service';
import { HbmProjectOpenerService } from './hbm-project-opener.service';

/** Configuration du tunnel courant pour un contexte HBM. */
interface HbmConfigurationData {
  HBMConfigurationId?: string;
}

/** Parcours vendeur de duplication du projet HomeByMe courant vers le même compte ou un autre compte client. */
@Injectable({ providedIn: 'root' })
export class HbmProjectDuplicationFlowService {
  private readonly modalService = inject(ModalService);
  private readonly customerService = inject(CustomerService);
  private readonly currentService = inject(CurrentService);
  private readonly projectService = inject(ProjectService);
  private readonly tunnelDataStore = inject(TunnelDataStore);
  private readonly hbmProjectOpener = inject(HbmProjectOpenerService);
  private readonly registry = inject(HbmConfiguratorRegistry);
  private readonly router = inject(Router);

  /** Indique si le projet courant est un projet HomeByMe du mode vendeur. */
  get isAvailable(): boolean {
    return (
      environment.vendor &&
      this.registry.byContext(this.customerService.context) !== undefined &&
      !!this.currentService.HBMProject
    );
  }

  /** Indique si un client est identifié, prérequis de la duplication. */
  get canStart(): boolean {
    return this.isAvailable && !!this.customerService.userDatas;
  }

  /** Démarre le parcours par le choix du compte client de destination. */
  async start(): Promise<void> {
    const project = await this.loadCurrentProject();
    if (!project) {
      return;
    }

    this.modalService.display(MODAL.PROJECT_DUPLICATE_TARGET, {
      project,
      onSelect: (target: ProjectDuplicateTarget) => this.onTargetSelected(project, target),
    });
  }

  private async loadCurrentProject(): Promise<Project | undefined> {
    const context = this.customerService.context as CONFIG_CONTEXT;
    const configurationData = this.tunnelDataStore.getCurrentStateValue().configurationData.get(context) as
      | HbmConfigurationData
      | undefined;
    const hbmUuid = configurationData?.HBMConfigurationId ?? this.currentService.HBMProject?.uuid;

    this.modalService.isLoading = true;
    try {
      const summary = await this.projectService.getSummary(hbmUuid.toUpperCase());
      return Object.assign(new Project(), {
        projectID: summary.projectID,
        projectUUID: summary.projectUUID,
        projectName: summary.projectName,
        distributionID: this.registry.byContext(context).distribID,
      });
    } catch (error) {
      logError(error);
      this.modalService.display(MODAL.GENERIC_ERROR, {});
      return undefined;
    } finally {
      this.modalService.isLoading = false;
    }
  }

  private onTargetSelected(project: Project, target: ProjectDuplicateTarget): void {
    if (target === ProjectDuplicateTarget.SameAccount) {
      this.displayRename(project, cloned => this.openOrRestore(cloned));
      return;
    }

    this.duplicateToOtherAccount(project);
  }

  private async duplicateToOtherAccount(project: Project): Promise<void> {
    const sourceSession = this.customerService.captureSession();
    const targetCustomer = await this.selectCustomer();
    if (!targetCustomer) {
      if (this.customerService.userDatas?.id !== sourceSession.userDatas?.id) {
        await this.restoreSession(sourceSession);
      }
      return;
    }

    this.displayRename(
      project,
      cloned => this.openOrRestore(cloned, sourceSession),
      () => this.restoreSession(sourceSession)
    );
  }

  private selectCustomer(): Promise<Customer | undefined> {
    return new Promise(resolve => {
      const onCustomerSelect = () => {
        const selectedCustomer = this.customerService.userDatas;
        this.modalService.close(true);
        resolve(selectedCustomer);
      };
      const onClose = () => {
        if (this.modalService.returnValue !== true) {
          resolve(undefined);
        }
      };

      this.modalService.display(MODAL.SEARCH_CUSTOMER, { onCustomerSelect, keepCurrentCustomer: true }, false, onClose);
    });
  }

  private displayRename(project: Project, onClose: (cloned: Project | null) => void, onError?: () => void): void {
    this.modalService.display(MODAL.PROJECT_DUPLICATE, { project, onClose, onError });
  }

  private async openOrRestore(cloned: Project | null, sourceSession?: CustomerSession): Promise<void> {
    if (!cloned) {
      if (sourceSession) {
        await this.restoreSession(sourceSession);
      }
      return;
    }

    try {
      this.tunnelDataStore.resetState(false);
      await this.hbmProjectOpener.open(
        this.hbmProjectOpener.getContext(cloned.distributionID),
        cloned.projectUUID,
        false,
        true
      );
    } catch (error) {
      logError(error);
      await this.router.navigate(['/']);
      this.modalService.display(MODAL.GENERIC_ERROR, {});
    }
  }

  private async restoreSession(session: CustomerSession): Promise<void> {
    this.customerService.restoreSession(session);
    await this.tunnelDataStore.loadCurrentCustomer(session.userDatas);
    if (!session.token) {
      return;
    }

    this.customerService.events.emit({
      type: 'CUSTOMER_TOKEN_ACQUIRED',
      data: {
        token: session.token,
        distributionID: environment.byMe[this.customerService.context]?.distribID,
        additionalInfo: {},
      },
    });
  }
}
